import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * A custom element that keeps the "special order" (Na zamówienie) treatment in sync
 * with the currently selected variant using a simple class toggle:
 *  - toggles `is-special-order` on itself (shows/hides the note)
 *  - toggles `add-to-cart-button--special-order` on the add-to-cart button
 *
 * The set of special-order variant ids is provided server-side via the
 * `data-special-order-variants` attribute, so no DOM morphing or re-rendering
 * is required on variant change.
 */
class SpecialOrderNote extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#apply(this.#currentVariantId());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * The list of special-order variant ids for this product.
   * @returns {string[]}
   */
  get #specialOrderVariantIds() {
    return (this.dataset.specialOrderVariants ?? '').split(',').filter(Boolean);
  }

  /**
   * Reads the currently selected variant id from the product form.
   * @returns {string | null}
   */
  #currentVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.closest('product-form-component')?.querySelector('[ref="variantId"]')
    );
    return input?.value || null;
  }

  /**
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (
      !event.detail.data.newProduct &&
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const variantId = event.detail.resource?.id;
    this.#apply(variantId != null ? String(variantId) : null);
  };

  /**
   * Toggles the special-order classes based on the given variant id.
   * @param {string | null} variantId
   */
  #apply(variantId) {
    const isSpecialOrder = variantId != null && this.#specialOrderVariantIds.includes(variantId);

    this.classList.toggle('is-special-order', isSpecialOrder);

    // The add-to-cart button node is replaced by product-form.js on variant change.
    // Defer to the next frame so this class toggle is applied after that swap.
    requestAnimationFrame(() => {
      const button = this.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
      button?.classList.toggle('add-to-cart-button--special-order', isSpecialOrder);
    });
  }
}

if (!customElements.get('special-order-note')) {
  customElements.define('special-order-note', SpecialOrderNote);
}
